import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaLeaf, FaCheck, FaPlay, FaUsers, FaShieldAlt, FaSun, FaLightbulb } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import trTherapyImage from '../../components/assets/Staff/TR Therapy 2.jpg';
import trTherapy1Image from '../../components/assets/Staff/TR Therapy 1.jpg';

const TRTherapy = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const overviewRef = useRef(null);
  const sessionRef = useRef(null);
  const designedRef = useRef(null);
  const whoRef = useRef(null);
  const lifestyleRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'session': sessionRef,
      'designed': designedRef,
      'who': whoRef,
      'lifestyle': lifestyleRef
    };

    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'session', ref: sessionRef },
        { id: 'designed', ref: designedRef },
        { id: 'who', ref: whoRef },
        { id: 'lifestyle', ref: lifestyleRef }
      ];

      const scrollPosition = window.scrollY + 100;

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'session', label: 'Session Experience', icon: FaPlay },
    { id: 'designed', label: 'Individual Sensations', icon: FaSun },
    { id: 'who', label: 'Who Chooses Targeted Radio Frequency', icon: FaUsers },
    { id: 'lifestyle', label: 'Balanced Lifestyle', icon: FaLeaf }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Comfort First",
      description: "Designed around one essential principle: your comfort and ease at every moment"
    },
    {
      icon: FaShieldAlt,
      title: "Advanced Radiofrequency",
      description: "Intelligently managed energy for stable, evenly distributed warmth with real-time feedback"
    },
    {
      icon: FaLeaf,
      title: "Deep Sensory Ease",
      description: "Gently warming, deeply soothing, calming and grounding — supportive of lightness and ease"
    }
  ];

  const sessionSensations = [
    "A sense of relaxation in the area",
    "Softer and more open sensations",
    "A feeling of ease that encourages natural movement",
    "A calm yet present state of body awareness"
  ];

  const whenPeopleChoose = [
    "Certain areas of the body feel constantly tight or resistant",
    "Sensations of stiffness return again and again even with regular movement",
    "The body feels easily overloaded by daily activities",
    "Comfort in movement feels limited or less fluid than it used to",
    "Gentle, non-invasive experiences feel more appropriate than intense approaches"
  ];

  const whoChoosesTR = [
    "Experience physical tension or heaviness after busy or demanding days",
    "Enjoy refined and non-invasive wellness experiences",
    "Value subtlety, comfort, and quality over intensity",
    "Want to reconnect with their body in a calm and considered way"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Targeted Radio Frequency" description="Targeted Radio Frequency (TR) therapy at Bioresonance Leicester – gentle warmth and body awareness. Non-invasive support at 108 New Walk." path="/therapy/tr-therapy" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Targeted Radio Frequency</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaSun className="mr-2" />
                Comfort, Warmth & Body Awareness
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Targeted Radio Frequency — A New Standard of Comfort, Warmth & Body Awareness
              </h1>

              <p className="text-xl text-dark-grey leading-relaxed">
                Discover what it feels like when modern technology meets deep sensory ease.
              </p>

              <p className="text-lg text-gray-600 leading-relaxed">
                Some experiences don't try to change your body — they simply create the right conditions for comfort, awareness, and connection. Targeted Radio Frequency is exactly that kind of experience.
              </p>

              <p className="text-lg text-gray-600 leading-relaxed">
                Using advanced radiofrequency technology, Targeted Radio Frequency delivers a controlled, pleasant warming sensation to selected areas of the body. The focus is not intensity, but ease, softness, and a sense of lightness that allows the body to settle naturally.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaHeart className="text-gold mr-2" />
                  <span>Comfort first</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaLeaf className="text-gold mr-2" />
                  <span>Your pace, your experience</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>

                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={trTherapyImage}
                      alt="Targeted Radio Frequency therapy"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What makes the Targeted Radio Frequency experience different?
              </h2>
              <p className="text-lg text-gray-600">
                Targeted Radio Frequency is designed around one essential principle: comfort first.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-gray-600 leading-relaxed text-center mb-4">
                The system uses intelligently managed radiofrequency energy to create a stable, evenly distributed warmth, continuously adjusted based on real-time feedback. This ensures a smooth, consistent, and personalized experience throughout the session.
              </p>
              <p className="text-gray-600 leading-relaxed text-center">
                People often describe the sensation as gently warming, deeply soothing, calming and grounding, and supportive of a feeling of lightness and ease. Individual experiences may vary.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Session Experience */}
      <section ref={sessionRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  What does a session feel like?
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    During a session, a handheld applicator is guided over the chosen area of the body. You'll notice a steady, comfortable warmth that builds gradually and remains pleasant at all times.
                  </p>
                  <p>
                    There is no pressure, no force, no abrupt sensations. Everything is guided by your comfort level and personal feedback.
                  </p>
                  <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                    <p className="text-dark-grey font-semibold text-lg mb-4">
                      Many people notice:
                    </p>
                    <ul className="space-y-2">
                      {sessionSensations.map((item, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <FaCheck className="text-gold mt-1 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-gray-600 italic">
                    The experience is unhurried, respectful, and entirely focused on how you feel.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl h-80">
                    <img
                      src={trTherapy1Image}
                      alt="Targeted Radio Frequency session experience"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Designed around individual sensations */}
      <section ref={designedRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-blue/10 text-blue px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaLightbulb className="mr-2" />
                Personalized Experience
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Designed around individual sensations
              </h2>
              <p className="text-lg text-gray-600">
                Targeted Radio Frequency allows for different styles of energy delivery, creating either a broader, surface-oriented warming sensation or a more focused, deeper sense of warmth within the area.
              </p>
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-gray-600 leading-relaxed text-center">
                These options are chosen based on preference and perception, allowing each session to feel intuitive and tailored — never standardized or routine.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who chooses Targeted Radio Frequency */}
      <section ref={whoRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Who chooses Targeted Radio Frequency?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                People are drawn to the Targeted Radio Frequency experience at moments when their body feels persistently uncomfortable, heavy, or tense — not just after a long day, but as a recurring part of everyday life.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-6">When do people often choose Targeted Radio Frequency?</h3>
                <p className="text-gray-600 mb-6">It's often chosen when:</p>
                <div className="space-y-4">
                  {whenPeopleChoose.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{item}</p>
                    </motion.div>
                  ))}
                </div>
                <p className="text-gray-600 mt-6 italic">
                  Rather than focusing on what's "wrong," Targeted Radio Frequency appeals to those who want a calmer, more respectful way of listening to their body — creating space for warmth, ease, and a softer relationship with ongoing physical sensations.
                </p>
              </div>

              <div>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20 mb-6">
                  <h3 className="text-2xl font-bold text-dark-grey mb-6">Targeted Radio Frequency is often chosen by people who:</h3>
                  <div className="space-y-4">
                    {whoChoosesTR.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="flex items-start space-x-3"
                      >
                        <div className="w-6 h-6 bg-blue/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <FaUsers className="text-blue text-sm" />
                        </div>
                        <p className="text-gray-600">{item}</p>
                      </motion.div>
                    ))}
                  </div>
                  <p className="text-gray-600 mt-6">
                    It appeals to those who appreciate technology that feels human — intuitive, responsive, and gentle.
                  </p>
                </div>
                <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                  <p className="text-gray-600 leading-relaxed">
                    Many people choose Targeted Radio Frequency not as a one-off experience, but as part of a long-term commitment to comfort, awareness, and quality of life — especially when their body asks for consistency rather than force.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A natural part of a balanced lifestyle */}
      <section ref={lifestyleRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                A natural part of a balanced lifestyle
              </h2>
              <p className="text-lg text-gray-600">
                For many people, Targeted Radio Frequency becomes part of a broader approach to everyday wellbeing. It fits naturally alongside practices that value presence, movement, breath, and conscious relaxation.
              </p>
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-gray-600 leading-relaxed text-center">
                The experience is simple, modern, and grounded in how the body feels — here and now.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            Your body. Your pace. Your experience.
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            If you're curious, the most meaningful way to understand Targeted Radio Frequency is to experience it yourself. Ready to explore how it feels? Book your session and discover your comfort zone.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Book Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default TRTherapy;
